import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  ChatCircleDots, 
  PaperPlaneTilt,
  Sparkle,
  ArrowRight,
  Lightning,
  Copy,
  Check,
  Clock,
  Plus,
  Trash,
  Rows,
  VideoCamera,
  Image,
  ArrowsClockwise,
  Rocket,
  X
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { ShareResultButton } from '../components/ShareResultButton';
import { useChatStore } from '../stores/aiToolsStore';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOOL_ID = 'marketing_coach';

const suggestedQuestions = [
  "How should I launch my new product on Instagram?",
  "What content strategy works best for B2B SaaS?",
  "Give me a 7-day content plan for growing followers",
  "How do I increase my email open rates?",
  "What's the best way to repurpose a blog post?"
];

const FeatureLink = ({ feature }) => {
  const featureMap = {
    'content_studio': { label: 'Content Studio', path: '/dashboard/content-studio' },
    'copy_studio': { label: 'Copy Studio', path: '/dashboard/copy-studio' },
    'competitor_spy': { label: 'Competitor Spy', path: '/dashboard/competitor-spy' },
    'daily_plan': { label: 'Daily Plan', path: '/dashboard/daily-plan' },
    'brand_analyzer': { label: 'Brand Analyzer', path: '/dashboard/brand-analyzer' },
    'seo_assistant': { label: 'SEO Assistant', path: '/dashboard/seo' },
    'email_assistant': { label: 'Email Assistant', path: '/dashboard/email' },
    'meta_ads': { label: 'Meta Ads', path: '/dashboard/meta-ads' },
    'carousel_creator': { label: 'Create Carousel', path: '/dashboard/carousel-creator' },
    'video_creator': { label: 'Create Video', path: '/dashboard/ad-creator' },
    'template_studio': { label: 'Create Flyer', path: '/dashboard/template-studio' },
    'flyer_creator': { label: 'Flyer Creator', path: '/dashboard/flyer-creator' },
    'character_creator': { label: 'Character Creator', path: '/dashboard/character-creator' },
    'voiceover': { label: 'Voiceover', path: '/dashboard/voiceover' },
  };
  const info = featureMap[feature];
  if (!info) return null;
  return (
    <a href={info.path} className="inline-flex items-center gap-1 text-xs bg-white/10 hover:bg-white/15 text-zinc-300 px-2 py-1 rounded-full transition-colors">
      {info.label} <ArrowRight size={10} />
    </a>
  );
};

export const MarketingCoachPage = () => {
  const chatStore = useChatStore();
  const chat = chatStore.getChat(TOOL_ID);
  const activeSession = chat.sessions.find(s => s.id === chat.activeSessionId);
  const messages = activeSession?.messages || [];
  const navigate = useNavigate();

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [batchModal, setBatchModal] = useState(null); // { message, action_items, features }
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!chat.activeSessionId || chat.sessions.length === 0) {
      chatStore.createSession(TOOL_ID, 'New Chat');
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  const handleSend = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    
    const sid = chat.activeSessionId;
    chatStore.addMessage(TOOL_ID, sid, { role: 'user', content: msg });
    setInput('');
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/chat/message`, {
        message: msg,
        session_id: sessionId
      });

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      chatStore.addMessage(TOOL_ID, sid, {
        role: 'assistant',
        content: data.result?.response || 'Sorry, I couldn\'t generate a response.',
        action_items: data.result?.action_items || [],
        related_features: data.result?.related_features || []
      });
    } catch {
      chatStore.addMessage(TOOL_ID, sid, {
        role: 'assistant',
        content: 'Sorry, there was an error. Please try again.',
        action_items: [],
        related_features: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewChat = useCallback(() => {
    chatStore.createSession(TOOL_ID, 'New Chat');
    setSessionId('');
    setInput('');
  }, []);

  const switchSession = useCallback((sid) => {
    chatStore.setActiveSession(TOOL_ID, sid);
    setHistoryOpen(false);
    setSessionId('');
  }, []);

  // ═══ Batch Create ═══
  const openBatchCreate = (msg) => {
    setBatchModal({ message: msg.content, action_items: msg.action_items || [], features: msg.related_features || [] });
    setBatchResult(null);
  };

  const generateBatch = async () => {
    if (!batchModal) return;
    setBatchLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/batch/prepare-content`, {
        message: batchModal.message,
        action_items: batchModal.action_items,
        features: batchModal.features,
      });
      setBatchResult(data.batch);
      toast.success('Content prepared for 3 formats!');
    } catch {
      toast.error('Failed to prepare batch content');
    }
    setBatchLoading(false);
  };

  const openCreator = (type) => {
    if (!batchResult) return;
    const storeType = type === 'ai_flyer' ? 'flyer' : type;
    const storeData = batchResult[storeType];
    sessionStorage.setItem('batch_prefill', JSON.stringify({ type, data: storeData }));
    setBatchModal(null);
    const paths = { carousel: '/dashboard/carousel-creator', video_ad: '/dashboard/ad-creator', flyer: '/dashboard/template-studio', ai_flyer: '/dashboard/template-studio' };
    navigate(paths[type]);
  };


  const hasContentFeatures = (msg) => {
    if (msg.role !== 'assistant') return false;
    const f = msg.related_features || [];
    const contentFeats = ['carousel_creator', 'video_creator', 'template_studio', 'flyer_creator'];
    return f.some(feat => contentFeats.includes(feat)) || (msg.action_items?.length > 0 && msg.content?.length > 100);
  };

  return (
    <DashboardLayout>
      <div data-testid="marketing-coach-page" className="flex flex-col" style={{ height: 'calc(100vh - 140px)' }}>
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <ChatCircleDots size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Marketing Coach</h1>
              <p className="text-zinc-400 text-xs">Chat history saved automatically</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setHistoryOpen(!historyOpen)} data-testid="chat-history-toggle"
              className="p-2 rounded-lg text-xs flex items-center gap-1.5 transition-all"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(245,240,232,0.4)' }}>
              <Clock size={14} /> {chat.sessions.length}
            </button>
            <button onClick={startNewChat} data-testid="new-chat-btn"
              className="p-2 rounded-lg text-xs flex items-center gap-1.5 transition-all"
              style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.15)', color: '#c8f060' }}>
              <Plus size={14} /> New
            </button>
          </div>
        </motion.div>

        {/* Chat History Panel */}
        {historyOpen && (
          <div className="mb-3 p-3 rounded-xl max-h-48 overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'rgba(245,240,232,0.25)' }}>Chat History</p>
            {chat.sessions.map(s => (
              <div key={s.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg cursor-pointer hover:bg-white/3 mb-0.5"
                onClick={() => switchSession(s.id)}
                style={{ background: s.id === chat.activeSessionId ? 'rgba(200,240,96,0.05)' : 'transparent' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs truncate" style={{ color: s.id === chat.activeSessionId ? '#c8f060' : 'rgba(245,240,232,0.5)' }}>
                    {s.messages[0]?.content?.slice(0, 50) || s.title}
                  </p>
                  <p className="text-[9px]" style={{ color: 'rgba(245,240,232,0.2)' }}>{s.messages.length} messages</p>
                </div>
                {s.id !== chat.activeSessionId && (
                  <button onClick={(e) => { e.stopPropagation(); chatStore.deleteSession(TOOL_ID, s.id); }}
                    className="p-1 hover:bg-white/5 rounded"><Trash size={10} style={{ color: 'rgba(255,68,68,0.4)' }} /></button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto rounded-2xl bg-zinc-900/30 border border-white/5 mb-4">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8">
              <div className="w-16 h-16 rounded-2xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center mb-6">
                <Sparkle size={28} className="text-zinc-400" />
              </div>
              <h2 className="text-lg font-medium font-['Instrument_Serif',serif] mb-2">Ask anything about marketing</h2>
              <p className="text-sm text-center mb-8 max-w-md">
                I know your brand profile and can give personalized advice on content, growth, ads, email, SEO, and more.
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-xl">
                {suggestedQuestions.map((q, i) => (
                  <button key={i} onClick={() => handleSend(q)} data-testid={`suggested-q-${i}`}
                    className="text-xs text-zinc-400 bg-zinc-800/50 hover:bg-zinc-700/50 border border-white/5 rounded-full px-3 py-2 transition-colors text-left">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {messages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] ${msg.role === 'user' 
                    ? 'bg-white text-black rounded-2xl rounded-tr-sm px-4 py-3' 
                    : 'bg-zinc-800/50 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3'}`}>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{typeof msg.content === 'object' ? JSON.stringify(msg.content) : msg.content}</div>
                    
                    {msg.role === 'assistant' && (
                      <div className="mt-2 flex justify-end">
                        <ShareResultButton
                          title="Marketing Insight"
                          content={(typeof msg.content === 'string' ? msg.content : '').slice(0, 400)}
                          type="Coach Insight"
                          variant="icon"
                        />
                      </div>
                    )}

                    {msg.action_items?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
                        <p className="text-xs font-medium text-zinc-400 flex items-center gap-1"><Lightning size={12} /> Action Items</p>
                        {msg.action_items.map((item, j) => (
                          <p key={j} className="text-xs text-zinc-300 pl-3">- {typeof item === 'object' ? (item.text || item.line || JSON.stringify(item)) : String(item)}</p>
                        ))}
                      </div>
                    )}

                    {msg.related_features?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {msg.related_features.map((f, j) => <FeatureLink key={j} feature={typeof f === 'string' ? f : ''} />)}
                      </div>
                    )}

                    {/* Batch Create Button */}
                    {hasContentFeatures(msg) && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <button onClick={() => openBatchCreate(msg)} data-testid={`batch-create-${i}`}
                          className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                          style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.15)' }}>
                          <Rocket size={14} weight="fill" /> Batch Create All Content
                        </button>
                      </div>
                    )}

                    {msg.role === 'assistant' && (
                      <button onClick={() => handleCopy(typeof msg.content === 'string' ? msg.content : '', i)}
                        className="mt-2 text-zinc-500 hover:text-zinc-300 transition-colors">
                        {copiedIdx === i ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-zinc-800/50 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-zinc-400 rounded-full animate-pulse" />
                      <div className="w-2 h-2 bg-zinc-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                      <div className="w-2 h-2 bg-zinc-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="bg-[#141414] border border-white/[0.07] rounded-[14px] p-3 flex items-end gap-3">
          <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
            data-testid="coach-input" placeholder="Ask me anything about marketing..."
            rows={1} className="flex-1 bg-transparent text-white placeholder:text-sm resize-none focus:outline-none max-h-32"
            style={{ minHeight: '36px' }} />
          <button onClick={() => handleSend()} disabled={!input.trim() || loading} data-testid="send-message-btn"
            className="bg-white text-black rounded-xl p-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0">
            <PaperPlaneTilt size={18} weight="fill" />
          </button>
        </div>

        {/* Batch Create Modal */}
        <AnimatePresence>
          {batchModal && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
              onClick={() => setBatchModal(null)}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                className="w-full max-w-lg mx-4 rounded-2xl overflow-hidden"
                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
                onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="flex items-center justify-between p-5 pb-0">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.08)' }}>
                      <Rocket size={18} weight="fill" style={{ color: '#c8f060' }} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold" style={{ color: '#f5f0e8' }}>Batch Create Content</h3>
                      <p className="text-[11px]" style={{ color: 'rgba(245,240,232,0.3)' }}>Turn this advice into 3 ready-to-use formats</p>
                    </div>
                  </div>
                  <button onClick={() => setBatchModal(null)} className="p-1.5 rounded-lg hover:bg-white/5">
                    <X size={16} style={{ color: 'rgba(245,240,232,0.3)' }} />
                  </button>
                </div>

                <div className="p-5">
                  {/* Source message preview */}
                  <div className="p-3 rounded-xl mb-5 text-xs leading-relaxed line-clamp-3"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.4)' }}>
                    {typeof batchModal.message === 'string' ? batchModal.message.slice(0, 200) : ''}...
                  </div>

                  {!batchResult ? (
                    /* Step 1: Generate */
                    <div className="text-center">
                      <p className="text-xs mb-4" style={{ color: 'rgba(245,240,232,0.4)' }}>
                        AI will create pre-filled content for each format based on this marketing advice.
                      </p>
                      <div className="flex gap-3 justify-center mb-5">
                        {[
                          { icon: Rows, label: 'Carousel', color: '#c8f060' },
                          { icon: VideoCamera, label: 'Video Ad', color: '#22d3ee' },
                          { icon: Image, label: 'Flyer', color: '#f59e0b' },
                        ].map(item => (
                          <div key={item.label} className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl"
                            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <item.icon size={20} style={{ color: item.color }} />
                            <span className="text-[10px] font-medium" style={{ color: 'rgba(245,240,232,0.5)' }}>{item.label}</span>
                          </div>
                        ))}
                      </div>
                      <button onClick={generateBatch} disabled={batchLoading} data-testid="generate-batch-btn"
                        className="px-6 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 mx-auto"
                        style={{ background: '#c8f060', color: '#080808', opacity: batchLoading ? 0.6 : 1 }}>
                        {batchLoading ? <><ArrowsClockwise size={14} className="animate-spin" /> Writing 3 formats…</> : <><Rocket size={14} weight="fill" /> Write All Content</>}
                      </button>
                    </div>
                  ) : (
                    <>
                    <div className="space-y-3">
                      <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: 'rgba(245,240,232,0.2)' }}>Content ready — click to open each creator:</p>
                      
                      {/* Carousel */}
                      <button onClick={() => openCreator('carousel')} data-testid="open-carousel-btn"
                        className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:border-opacity-30"
                        style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.1)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(200,240,96,0.08)' }}>
                          <Rows size={18} style={{ color: '#c8f060' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold mb-0.5" style={{ color: '#c8f060' }}>Carousel Post</p>
                          <p className="text-[11px] truncate" style={{ color: 'rgba(245,240,232,0.4)' }}>
                            {batchResult.carousel?.cover?.headline || batchResult.carousel?.topic || 'Ready to create'}
                          </p>
                        </div>
                        <ArrowRight size={16} style={{ color: '#c8f060' }} />
                      </button>

                      {/* Video Ad */}
                      <button onClick={() => openCreator('video_ad')} data-testid="open-video-btn"
                        className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:border-opacity-30"
                        style={{ background: 'rgba(34,211,238,0.03)', border: '1px solid rgba(34,211,238,0.1)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(34,211,238,0.08)' }}>
                          <VideoCamera size={18} style={{ color: '#22d3ee' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold mb-0.5" style={{ color: '#22d3ee' }}>Video Ad</p>
                          <p className="text-[11px] truncate" style={{ color: 'rgba(245,240,232,0.4)' }}>
                            {batchResult.video_ad?.headline || 'Ready to create'}
                          </p>
                        </div>
                        <ArrowRight size={16} style={{ color: '#22d3ee' }} />
                      </button>

                      {/* Flyer */}
                      <button onClick={() => openCreator('flyer')} data-testid="open-flyer-btn"
                        className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:border-opacity-30"
                        style={{ background: 'rgba(245,158,11,0.03)', border: '1px solid rgba(245,158,11,0.1)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(245,158,11,0.08)' }}>
                          <Image size={18} style={{ color: '#f59e0b' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold mb-0.5" style={{ color: '#f59e0b' }}>Flyer (Template)</p>
                          <p className="text-[11px] truncate" style={{ color: 'rgba(245,240,232,0.4)' }}>
                            {batchResult.flyer?.headline || 'Ready to create'}
                          </p>
                        </div>
                        <ArrowRight size={16} style={{ color: '#f59e0b' }} />
                      </button>

                      {/* Custom Flyer */}
                      <button onClick={() => openCreator('ai_flyer')} data-testid="open-ai-flyer-btn"
                        className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all hover:border-opacity-30"
                        style={{ background: 'rgba(168,139,250,0.03)', border: '1px solid rgba(168,139,250,0.1)' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(168,139,250,0.08)' }}>
                          <Sparkle size={18} weight="fill" style={{ color: '#a78bfa' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold mb-0.5" style={{ color: '#a78bfa' }}>Custom Flyer</p>
                          <p className="text-[11px] truncate" style={{ color: 'rgba(245,240,232,0.4)' }}>
                            {batchResult.flyer?.headline || 'AI-designed with your content'}
                          </p>
                        </div>
                        <ArrowRight size={16} style={{ color: '#a78bfa' }} />
                      </button>
                    </div>
                  </>
                )}
              </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default MarketingCoachPage;
